import {
  Mail,
  Phone,
  ChevronLeft,
  Loader2,
  RefreshCw,
  Calendar,
  MapPin,
  FileText,
  Download,
  X,
  Clock,
  Users,
} from "lucide-react";
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";

const DEFAULT_IMAGE = "/assets/images/broken.png";

function formatEventDate(isoString) {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatCategory(value) {
  if (!value) return "";
  return value
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function formatMime(mimeType) {
  if (!mimeType) return "FILE";
  if (mimeType === "application/pdf") return "PDF";
  if (mimeType === "application/msword") return "DOC";
  if (mimeType.includes("wordprocessingml")) return "DOCX";
  if (mimeType.startsWith("image/")) return mimeType.split("/")[1].toUpperCase();
  return mimeType.split("/")[1]?.toUpperCase() ?? "FILE";
}

async function downloadFile(url, filename) {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error();
    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = filename || "download";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  } catch {
    // silently fail — caller shows toast if needed
  }
}

// ── Event Detail Modal ─────────────────────────────────────────────────────────
function EventModal({ event, onClose }) {
  if (!event) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cover */}
        <div className="h-48 bg-gray-100 relative">
          {event.coverImageUrl ? (
            <img
              src={event.coverImageUrl}
              alt={event.title}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.src = DEFAULT_IMAGE; }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Calendar size={40} className="text-gray-300" />
            </div>
          )}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-1.5 rounded-full bg-white/80 hover:bg-white text-gray-600 transition"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {event.category && (
            <span className="inline-block text-[10px] font-popinsMedium uppercase tracking-wider bg-themeColor/10 text-themeColor px-2 py-0.5 rounded mb-3">
              {event.category}
            </span>
          )}
          <h2 className="font-popinsSemiBold text-lg text-gray-800 leading-snug">
            {event.title}
          </h2>
          {event.description && (
            <p className="font-popinsRegular text-sm text-gray-500 mt-2 leading-relaxed">
              {event.description}
            </p>
          )}
          <div className="mt-4 space-y-2">
            <div className="flex items-center gap-2 text-sm text-gray-600 font-popinsRegular">
              <Calendar size={14} className="flex-shrink-0 text-themeColor" />
              <span>{formatEventDate(event.scheduledAt)}</span>
            </div>
            {event.scheduledAt && (
              <div className="flex items-center gap-2 text-sm text-gray-600 font-popinsRegular">
                <Clock size={14} className="flex-shrink-0 text-themeColor" />
                <span>
                  {new Date(event.scheduledAt).toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>
            )}
            {event.location && (
              <div className="flex items-center gap-2 text-sm text-gray-600 font-popinsRegular">
                <MapPin size={14} className="flex-shrink-0 text-themeColor" />
                <span>{event.location}</span>
              </div>
            )}
            {event.maxVolunteers != null && (
              <div className="flex items-center gap-2 text-sm text-gray-600 font-popinsRegular">
                <Users size={14} className="flex-shrink-0 text-themeColor" />
                <span>Max volunteers: {event.maxVolunteers}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Upcoming Events Grid ───────────────────────────────────────────────────────
function EventsGrid({ events }) {
  const [selectedEvent, setSelectedEvent] = useState(null);

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {events.map((event) => (
          <div
            key={event.id}
            className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setSelectedEvent(event)}
          >
            {/* Cover image */}
            <div className="h-[130px] bg-gray-100 overflow-hidden">
              {event.coverImageUrl ? (
                <img
                  src={event.coverImageUrl}
                  alt={event.title}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = DEFAULT_IMAGE; }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Calendar size={32} className="text-gray-300" />
                </div>
              )}
            </div>

            {/* Details */}
            <div className="p-4">
              {event.category && (
                <span className="inline-block text-[10px] font-popinsMedium uppercase tracking-wider bg-themeColor/10 text-themeColor px-2 py-0.5 rounded mb-2">
                  {formatCategory(event.category)}
                </span>
              )}
              <p className="font-popinsSemiBold text-sm text-gray-800 line-clamp-2 leading-snug">
                {event.title}
              </p>
              <div className="flex flex-col gap-1 mt-3">
                <div className="flex items-center gap-1.5 text-xs text-gray-500 font-popinsRegular">
                  <Calendar size={12} className="flex-shrink-0" />
                  <span>{formatEventDate(event.scheduledAt)}</span>
                </div>
                {event.location && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 font-popinsRegular">
                    <MapPin size={12} className="flex-shrink-0" />
                    <span className="line-clamp-1">{event.location}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {selectedEvent && (
        <EventModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
      )}
    </>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function MainCollaborate({ nonprofit, isLoading, error, onRetry }) {
  const [profileImgLoaded, setProfileImgLoaded] = useState(false);

  const services = useMemo(() => {
    if (!nonprofit?.services || !Array.isArray(nonprofit.services)) return [];
    return nonprofit.services.map((service) => service.name);
  }, [nonprofit]);

  const contactName = useMemo(() => {
    if (!nonprofit?.contact) return "Not provided";
    const parts = [nonprofit.contact.firstName, nonprofit.contact.lastName].filter(Boolean);
    return parts.length ? parts.join(" ") : "Not provided";
  }, [nonprofit]);

  const activeSince = nonprofit?.createdAt
    ? new Date(nonprofit.createdAt).getFullYear()
    : null;

  const upcomingEvents = nonprofit?.upcomingEvents ?? [];
  const publicDocuments = nonprofit?.publicDocuments ?? [];

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="animate-spin text-themeColor" size={36} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-4 py-20">
        <p className="font-popinsMedium text-black/70">{error}</p>
        <button
          onClick={onRetry}
          className="flex items-center gap-2 px-4 py-2 border rounded-md font-popinsSemiBold"
        >
          <RefreshCw size={16} />
          Retry
        </button>
      </div>
    );
  }

  if (!nonprofit) return null;

  return (
    <div className="flex flex-col justify-between mb-5 gap-8">
      <div className="w-full">
        {/* Header */}
        <div className="flex items-center gap-x-1">
          <Link to={"/dashboard/explore-nonprofit"}>
            <ChevronLeft size={24} />
          </Link>
          <h1 className="font-popinsSemiBold text-2xl">
            NGO Profile & Collaborate
          </h1>
        </div>

        {/* Profile picture + org info */}
        <div className="flex gap-x-3 mt-6">
          <div className="w-[250px] h-[160px] rounded-lg overflow-hidden bg-gray-100 relative flex-shrink-0">
            {nonprofit.profilePictureUrl ? (
              <>
                {!profileImgLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-themeColor" />
                  </div>
                )}
                <img
                  src={nonprofit.profilePictureUrl}
                  alt="Organization logo"
                  className="relative w-full h-full object-cover"
                  onLoad={() => setProfileImgLoaded(true)}
                  onError={(e) => { e.target.src = DEFAULT_IMAGE; setProfileImgLoaded(true); }}
                />
              </>
            ) : (
              <img
                src={DEFAULT_IMAGE}
                alt="Organization logo"
                className="w-full h-full object-cover"
              />
            )}
          </div>
          <div>
            <p className="font-popinsSemiBold text-base lg:text-lg">
              {nonprofit.organizationName}
            </p>
            <p className="font-popinsRegular text-black/50 text-sm mt-1 lg:mt-0">
              {nonprofit.address || "Address not provided"}
            </p>
            {activeSince && (
              <p className="font-popinsRegular text-black/50 text-sm">
                Active since {activeSince}
              </p>
            )}
          </div>
        </div>

        {/* Contact Person */}
        <div className="mt-6">
          <p className="text-xs uppercase tracking-wide text-black/50 font-popinsMedium">
            Contact Person
          </p>
          <p className="font-popinsSemiBold text-lg mt-1 text-black">{contactName}</p>
        </div>

        {/* Mission */}
        <div className="mt-6">
          <p className="font-popinsSemiBold text-base">Mission</p>
          {nonprofit.mission ? (
            <p className="font-popinsRegular text-sm text-black/70 pr-6 mt-1">
              {nonprofit.mission}
            </p>
          ) : (
            <p className="font-popinsRegular text-sm text-black/40 pr-6 mt-1 italic">
              No mission statement provided.
            </p>
          )}
        </div>

        {/* Services */}
        <div className="mt-5">
          <p className="font-popinsSemiBold text-base">Services Offered</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {services.length > 0 ? (
              services.map((serviceName, index) => (
                <div
                  key={`${serviceName}-${index}`}
                  className="bg-themeColor text-white font-popinsSemiBold text-sm px-5 py-2 rounded-lg"
                >
                  {serviceName}
                </div>
              ))
            ) : (
              <p className="font-popinsRegular text-sm text-black/50">
                No services listed
              </p>
            )}
          </div>
        </div>

        {/* Contact */}
        <div className="mt-5">
          <p className="font-popinsSemiBold text-base">Contact</p>
          <div className="flex items-center gap-x-3 mt-3">
            <div className="bg-gray-100 p-1.5 rounded-md">
              <Mail size={20} />
            </div>
            <a
              href={nonprofit.contact?.email ? `mailto:${nonprofit.contact.email}` : undefined}
              className="font-popinsRegular text-base text-black hover:text-red-500 transition-colors"
            >
              {nonprofit.contact?.email || "Not provided"}
            </a>
          </div>
          <div className="flex items-center gap-x-3 mt-3">
            <div className="bg-gray-100 p-1.5 rounded-md">
              <Phone size={20} />
            </div>
            <a
              href={nonprofit.phoneNumber ? `tel:${nonprofit.phoneNumber}` : undefined}
              className="font-popinsRegular text-base text-black hover:text-red-500 transition-colors"
            >
              {nonprofit.phoneNumber || "Not provided"}
            </a>
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="mt-5">
          <p className="font-popinsSemiBold text-base">Upcoming Events</p>
          {upcomingEvents.length > 0 ? (
            <div className="mt-4">
              <EventsGrid events={upcomingEvents} />
            </div>
          ) : (
            <p className="font-popinsRegular text-sm text-black/40 mt-2 italic">
              No upcoming events scheduled.
            </p>
          )}
        </div>

        {/* Shared Documents */}
        {publicDocuments.length > 0 && (
          <div className="mt-5">
            <p className="font-popinsSemiBold text-base">Shared Documents</p>
            <div className="mt-3 space-y-2">
              {publicDocuments.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText size={16} className="text-gray-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-popinsRegular text-sm text-gray-800 truncate">
                        {doc.filename || "Document"}
                      </p>
                      <span className="text-[10px] font-popinsMedium bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded uppercase">
                        {formatMime(doc.mimeType)}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => downloadFile(doc.documentLink, doc.filename)}
                    title="Download"
                    className="ml-3 text-gray-400 hover:text-themeColor transition-colors flex-shrink-0"
                  >
                    <Download size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
